import React, { createContext, useContext, useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, get, set, push, remove, onValue, off } from 'firebase/database';
import { useAuth } from './AuthContext';

const OrgChartContext = createContext();

export function useOrgChart() {
  return useContext(OrgChartContext);
}

export function OrgChartProvider({ children }) {
  const [orgNodes, setOrgNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser, isAdmin } = useAuth();

  // Fetch organizational chart data from Firebase
  useEffect(() => {
    setLoading(true);
    setError('');
    
    const orgChartRef = ref(database, 'orgChart');
    
    // Create a realtime listener for org chart data
    const unsubscribe = onValue(orgChartRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const orgChartData = snapshot.val();
          const nodesList = Object.keys(orgChartData)
            .map(key => ({
              id: key,
              name: orgChartData[key].name || '',
              title: orgChartData[key].title || '',
              parentId: orgChartData[key].parentId || null,
              imageBase64: orgChartData[key].imageBase64 || null,
              order: orgChartData[key].order || 0,
              createdAt: orgChartData[key].createdAt || Date.now()
            }));
          
          setOrgNodes(nodesList);
        } else {
          // No org chart exists, not an error
          setOrgNodes([]);
        }
        setError('');
      } catch (err) {
        console.error('Error processing org chart data:', err);
        setError('Failed to process organizational chart data');
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error('Error fetching org chart:', err);
      setError('Failed to load organizational chart: ' + err.message);
      setLoading(false);
    });
    
    // Clean up the listener when the component unmounts
    return () => {
      off(orgChartRef);
    };
  }, []);

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Add a new org chart node
  const addNode = async (name, title, parentId, imageFile) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can modify the organizational chart');
    }

    try {
      // Get current node count for ordering
      const orgChartRef = ref(database, 'orgChart');
      
      // Convert image to base64 if provided
      let imageBase64 = null;
      if (imageFile) {
        imageBase64 = await convertToBase64(imageFile);
      }
      
      const newNodeRef = push(orgChartRef);
      
      const node = {
        name,
        title,
        parentId,
        imageBase64,
        createdAt: Date.now(),
        order: getNextOrder(parentId)
      };
      
      await set(newNodeRef, node);
      
      return { success: true, id: newNodeRef.key };
    } catch (err) {
      console.error('Error adding org chart node:', err);
      throw err;
    }
  };
  
  // Get the next order value for a new node
  const getNextOrder = (parentId) => {
    // Find all siblings (nodes with the same parentId)
    const siblings = orgNodes.filter(node => node.parentId === parentId);
    
    // Return the next order (max + 1 or 0 if no siblings)
    if (siblings.length === 0) {
      return 0;
    }
    
    return Math.max(...siblings.map(node => node.order || 0)) + 1;
  };

  // Edit an existing org chart node
  const editNode = async (id, { name, title, parentId, imageFile }) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can modify the organizational chart');
    }

    try {
      const nodeRef = ref(database, `orgChart/${id}`);
      
      // Get the current data first
      const snapshot = await get(nodeRef);
      if (!snapshot.exists()) {
        throw new Error('Organization chart node not found');
      }
      
      const currentData = snapshot.val();
      
      // Process new image if provided
      let imageBase64 = currentData.imageBase64;
      if (imageFile) {
        imageBase64 = await convertToBase64(imageFile);
      }
      
      // Check if parentId has changed
      let order = currentData.order || 0;
      if (parentId !== currentData.parentId) {
        // Get a new order for the node in its new parent
        order = getNextOrder(parentId);
      }
      
      // Update data
      const updatedData = {
        ...currentData,
        name: name !== undefined ? name : currentData.name,
        title: title !== undefined ? title : currentData.title,
        parentId: parentId !== undefined ? parentId : currentData.parentId,
        imageBase64,
        order
      };
      
      await set(nodeRef, updatedData);
      
      return { success: true };
    } catch (err) {
      console.error('Error editing org chart node:', err);
      throw err;
    }
  };

  // Delete an org chart node
  const deleteNode = async (id) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can modify the organizational chart');
    }

    try {
      // Check if node has children
      const children = orgNodes.filter(node => node.parentId === id);
      if (children.length > 0) {
        throw new Error('Cannot delete a node with children. Please remove or reassign the children first.');
      }
      
      // Delete the node from the database
      const nodeRef = ref(database, `orgChart/${id}`);
      await remove(nodeRef);
      
      return { success: true };
    } catch (err) {
      console.error('Error deleting org chart node:', err);
      throw err;
    }
  };
  
  // Reorder siblings at the same level
  const reorderNodes = async (parentId, reorderedIds) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can modify the organizational chart');
    }

    try {
      const updates = {};
      
      reorderedIds.forEach((id, index) => {
        updates[`orgChart/${id}/order`] = index;
      });
      
      // Update the database
      const dbRef = ref(database);
      await set(dbRef, updates);
      
      return { success: true };
    } catch (err) {
      console.error('Error reordering org chart nodes:', err);
      throw err;
    }
  };

  // Utility function to get a hierarchical representation of the org chart
  const getHierarchicalOrgChart = () => {
    // Create a copy of the nodes for processing
    const nodes = [...orgNodes];
    
    // Sort nodes by order within their parent groups
    nodes.sort((a, b) => {
      // First sort by parentId (null parents come first)
      if (a.parentId === null && b.parentId !== null) return -1;
      if (a.parentId !== null && b.parentId === null) return 1;
      if (a.parentId !== b.parentId) return a.parentId < b.parentId ? -1 : 1;
      
      // Then sort by order
      return a.order - b.order;
    });
    
    // Function to build the tree structure
    const buildTree = (parentId = null) => {
      return nodes
        .filter(node => node.parentId === parentId)
        .map(node => ({
          ...node,
          children: buildTree(node.id)
        }));
    };
    
    // Return the full hierarchy starting from root nodes (parentId = null)
    return buildTree();
  };
  
  // Get a flat list of all nodes for dropdowns and UI selection
  const getAllNodesFlat = () => {
    return [...orgNodes];
  };

  const value = {
    orgNodes,
    loading,
    error,
    addNode,
    editNode,
    deleteNode,
    reorderNodes,
    getHierarchicalOrgChart,
    getAllNodesFlat
  };

  return (
    <OrgChartContext.Provider value={value}>
      {children}
    </OrgChartContext.Provider>
  );
} 